import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, ShieldCheck, Truck } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/catalog/ProductCard";
import HeroSlider from "@/components/home/HeroSlider";
import FeaturedProductsSkeleton from "@/components/home/FeaturedProductsSkeleton";
import SEOHead from "@/components/SEOHead";
import { useFeaturedProducts } from "@/hooks/useFeaturedProducts";
import { useHomeSectionImages } from "@/hooks/useHomeSectionImages";
import heroImage from "@/assets/hero-dog.jpg";
import catImage from "@/assets/necesita_ayuda.webp";

const Home = () => {
  const { data: featuredProducts = [], isLoading: loadingFeatured } = useFeaturedProducts(4);
  const { data: sectionImages = [] } = useHomeSectionImages();

  // Get pet type images from database or fallback to local
  const dogSectionImage = sectionImages.find((img) => img.section_key === "pet_type_dog");
  const catSectionImage = sectionImages.find((img) => img.section_key === "pet_type_cat");

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Productos Premium para Mascotas"
        description="Tu tienda de productos premium para mascotas en Costa Rica. Alimentos, juguetes, accesorios y más para perros y gatos. Envío rápido y atención personalizada."
        keywords="tienda mascotas, alimento perros, alimento gatos, accesorios mascotas, Costa Rica, Gordon Store"
        breadcrumbs={[{ name: "Inicio", url: "/" }]}
      />
      <Header />

      {/* Hero Slider */}
      <HeroSlider />

      {/* Features Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-3 p-6 rounded-3xl bg-background shadow-soft hover:shadow-card transition-all hover:scale-105">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pastel-green/20 mb-2">
                <Truck className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold">Envío Rápido</h3>
              <p className="text-muted-foreground">Recibí tus productos en tiempo récord</p>
            </div>

            <div className="text-center space-y-3 p-6 rounded-3xl bg-background shadow-soft hover:shadow-card transition-all hover:scale-105">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pastel-pink/20 mb-2">
                <ShieldCheck className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold">Calidad Garantizada</h3>
              <p className="text-muted-foreground">Solo productos premium para tu mascota</p>
            </div>

            <div className="text-center space-y-3 p-6 rounded-3xl bg-background shadow-soft hover:shadow-card transition-all hover:scale-105">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/20 mb-2">
                <Heart className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold">Atención Personalizada</h3>
              <p className="text-muted-foreground">Asesoramiento experto para cada necesidad</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Pet Type Section */}
      <section className="py-16 bg-gradient-to-br from-pastel-peach/20 via-background to-pastel-blue/20 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="confetti-dot bg-pastel-green top-10 left-20" />
        <div className="confetti-dot bg-accent bottom-10 right-20" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprá por Mascota</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Encontrá todo lo que tu compañero peludo necesita
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Dogs Card */}
            <Link
              to="/catalogo?petType=dog"
              className="group relative overflow-hidden rounded-3xl shadow-card hover:shadow-lg transition-all hover:scale-105"
            >
              <div className="relative h-[400px]">
                {/* Background organic shape */}
                <div className="absolute inset-0 bg-pastel-blue organic-shape opacity-40 scale-110" />

                {/* Image */}
                <img
                  src={dogSectionImage?.image_url || heroImage}
                  alt={dogSectionImage?.alt_text || "Productos para perros - Gordon Store Costa Rica"}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
                  <h3 className="text-3xl font-bold mb-3 text-foreground">
                    {dogSectionImage?.title || "Productos para Perros"}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {dogSectionImage?.subtitle || "Todo lo que tu perro necesita para estar feliz y saludable"}
                  </p>
                  <Button variant="pill" size="lg">
                    Ver Productos
                    <ArrowRight className="ml-2" />
                  </Button>
                </div>
              </div>
            </Link>

            {/* Cats Card */}
            <Link
              to="/catalogo?petType=cat"
              className="group relative overflow-hidden rounded-3xl shadow-card hover:shadow-lg transition-all hover:scale-105"
            >
              <div className="relative h-[400px]">
                {/* Background organic shape */}
                <div className="absolute inset-0 bg-pastel-pink organic-shape-alt opacity-40 scale-110" />

                {/* Image */}
                <img
                  src={catSectionImage?.image_url || catImage}
                  alt={catSectionImage?.alt_text || "Productos para gatos - Gordon Store Costa Rica"}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
                  <h3 className="text-3xl font-bold mb-3 text-foreground">
                    {catSectionImage?.title || "Productos para Gatos"}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {catSectionImage?.subtitle || "Mimá a tu gato con los mejores productos del mercado"}
                  </p>
                  <Button variant="pill" size="lg">
                    Ver Productos
                    <ArrowRight className="ml-2" />
                  </Button>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products - Skeleton o contenido */}
      {loadingFeatured ? (
        <FeaturedProductsSkeleton />
      ) : (
        featuredProducts.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Productos Destacados</h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Descubrí nuestra selección de productos favoritos para tu mascota
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {featuredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    slug={product.slug}
                    price={product.base_price}
                    image={product.image_url || ""}
                    category={product.category?.name || ""}
                    variations={product.variations}
                  />
                ))}
              </div>

              <div className="text-center">
                <Button asChild variant="outline" size="lg">
                  <Link to="/catalogo">Ver Todos los Productos</Link>
                </Button>
              </div>
            </div>
          </section>
        )
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-pastel-green/20 via-background to-pastel-peach/20 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="confetti-dot bg-primary top-10 right-20" />
        <div className="confetti-dot bg-secondary bottom-20 left-20" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-pastel-pink organic-shape-alt opacity-40 -z-10 scale-110" />
              <img
                src={catImage}
                alt="Gato hermoso - Gordon Store Costa Rica"
                loading="lazy"
                decoding="async"
                className="w-full h-auto rounded-3xl shadow-card"
              />
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold">¿Necesitás asesoramiento?</h2>
              <p className="text-lg text-muted-foreground">
                Nuestro equipo de expertos está listo para ayudarte a elegir los mejores productos para tu mascota.
              </p>
              <Button asChild size="lg" variant="pill">
                <Link to="/contacto">
                  Contactanos por WhatsApp
                  <ArrowRight className="ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
